import { useEffect, useState } from 'react'

import { useSystemConfigGetApi, useSystemConfigUpdateApi } from '@/common/api/modules/systemConfig'
import { useShowSavedToast } from '@/common/app/toast'
import { SystemConfig } from '@/common/model/systemConfig'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Tab from '@/components/view/atoms/tab/Tab'
import TabList from '@/components/view/atoms/tab/TabList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

// SearchSuggestSettingsPage と同じ入力欄クラス（w-full は使用箇所で付与）
const textInput =
  'shadow-inset text-size-13 rounded-lg border border-border-input bg-input px-3 py-2'

// バージョン（V5 / V6）を切り替えて、それぞれのリンクルールを保持する
type Version = 'v5' | 'v6'

// リンクルールを持つセクション（商品はリンクマスタ連動のため入力欄なし）
interface LinkRules {
  article: string
  productList: string
  featureCategory: string
  cart: string
  brand: string
}

// URL に埋め込む変数の参照元（どのテーブルのどのカラムか）
interface ParamSource {
  token: string
  label: string
  table: string
  column: string
}

// ドメイン部分は常に「サイト管理 > サイトURL」（sites.domain）に置換する共通変数
const SITE_URL_SOURCE: ParamSource = {
  token: '{site_url}',
  label: 'サイトURL（ドメイン）',
  table: 'sites',
  column: 'domain'
}

// 参照元パラメータの定義（使い回し）
const FILE_DIR: ParamSource = {
  token: '{file_dir_path}',
  label: 'ディレクトリパス',
  table: 'w2_feature_page',
  column: 'file_dir_path'
}
const FILE_NAME: ParamSource = {
  token: '{file_name}',
  label: 'ファイル名（拡張子含む）',
  table: 'w2_feature_page',
  column: 'file_name'
}
const PRODUCT_CATEGORY_ID: ParamSource = {
  token: '{category_id}',
  label: '商品カテゴリID',
  table: 'w2_product_category',
  column: 'category_id'
}
const FEATURE_CATEGORY_ID: ParamSource = {
  token: '{category_id}',
  label: '特集カテゴリID',
  table: 'w2_feature_page_category',
  column: 'category_id'
}
const PRODUCT_ID: ParamSource = {
  token: '{product_id}',
  label: '商品ID',
  table: 'w2_product',
  column: 'product_id'
}
const VARIATION_ID: ParamSource = {
  token: '{variation_id}',
  label: '商品バリエーションID',
  table: 'w2_product_stock',
  column: 'variation_id'
}
const BRAND_ID: ParamSource = {
  token: '{brand_id}',
  label: 'ブランドID',
  table: 'w2_product_brand',
  column: 'brand_id'
}

interface LinkDef {
  key: keyof LinkRules
  heading: string
  // ドメイン=｛site_url｝・パラメータ以外の「製品固有パス」を含む、バージョン別の実テンプレート
  template: Record<Version, string>
  params: Record<Version, ParamSource[]>
  note?: Partial<Record<Version, string>>
}

const LINK_DEFS: LinkDef[] = [
  {
    key: 'article',
    heading: '記事へのリンク（特集ページ）',
    template: {
      // file_dir_path はサイトルートからのパス（実データ例 Page¥Feature¥）を丸ごと持つため、
      // /Page/ や /pages/feature/ のような接頭辞を足すとディレクトリが二重になる。
      // ディレクトリとファイル名の間のスラッシュは必須（無いと dirnamefile.html になる）
      v5: '{site_url}/{file_dir_path}/{file_name}',
      v6: '{site_url}/{file_dir_path}/{file_name}'
    },
    params: {
      v5: [FILE_DIR, FILE_NAME],
      v6: [FILE_DIR, FILE_NAME]
    }
  },
  {
    key: 'productList',
    heading: '商品一覧ページへのリンク（商品カテゴリ）',
    template: {
      v5: '{site_url}/Form/Product/ProductList.aspx?shop=0&cat={category_id}&dpcnt=15&img=1&sort=07&swrd=a&udns=0&fpfl=0&sfl=0&pno=1',
      v6: '{site_url}/product/list?cat={category_id}&dpcnt=10&swrd=a&fpfl=0&sfl=0&egfl=0&pno=1'
    },
    params: {
      v5: [PRODUCT_CATEGORY_ID],
      v6: [PRODUCT_CATEGORY_ID]
    },
    note: {
      v6: 'スラッグURL（スラックURL）利用時：{site_url}/product/list/{category_url}/（{category_url} = w2_product_category.url）'
    }
  },
  {
    key: 'featureCategory',
    heading: '特集カテゴリへのリンク（記事カテゴリ）',
    template: {
      v5: '{site_url}/Form/FeaturePage/FeaturePageList.aspx?fpcid={category_id}',
      v6: '{site_url}/feature/list/{category_id}'
    },
    params: {
      v5: [FEATURE_CATEGORY_ID],
      v6: [FEATURE_CATEGORY_ID]
    }
  },
  {
    key: 'cart',
    heading: 'カート投入リンク',
    template: {
      v5: '{site_url}/Form/Order/CartList.aspx?ckbn=1&pid1={product_id}&vid1={variation_id}&prdcnt1=1',
      v6: '{site_url}/cart/direct-add?caa=1&pid1={product_id}&vid1={variation_id}&pqty1=1'
    },
    params: {
      v5: [PRODUCT_ID, VARIATION_ID],
      v6: [PRODUCT_ID, VARIATION_ID]
    }
  },
  {
    key: 'brand',
    heading: 'ブランドTOPリンク',
    template: {
      v5: '{site_url}/DefaultBrandTop.aspx?bid={brand_id}',
      v6: '{site_url}/brand/{brand_id}'
    },
    params: {
      v5: [BRAND_ID],
      v6: [BRAND_ID]
    }
  }
]

// 初期値（ドメイン変数化済みの実テンプレート）。V5 / V6 で製品固有パスが異なる。
const initialRules = (v: Version): LinkRules => ({
  article: LINK_DEFS[0].template[v],
  productList: LINK_DEFS[1].template[v],
  featureCategory: LINK_DEFS[2].template[v],
  cart: LINK_DEFS[3].template[v],
  brand: LINK_DEFS[4].template[v]
})

const VERSIONS: Version[] = ['v5', 'v6']

// SystemConfig（グローバル・全サイト共通）でのキー命名
const cfgKey = (v: Version, key: keyof LinkRules) => `w2link.${v}.${key}`

const emptyAllRules = (): Record<Version, LinkRules> => ({
  v5: initialRules('v5'),
  v6: initialRules('v6')
})

// 保存済み SystemConfig から現在値を構築（未保存キーは製品固有パスの既定値へフォールバック）
const buildRules = (configs?: SystemConfig[]): Record<Version, LinkRules> => {
  const map = new Map((configs ?? []).map((c) => [c.key, c.value]))
  const forVersion = (v: Version): LinkRules =>
    LINK_DEFS.reduce((acc, d) => {
      const k = cfgKey(v, d.key)
      acc[d.key] = map.has(k) ? (map.get(k) as string) : d.template[v]
      return acc
    }, {} as LinkRules)
  return { v5: forVersion('v5'), v6: forVersion('v6') }
}

// 使用変数と参照元（テーブル.カラム）の一覧
const ParamRefs = ({ params }: { params: ParamSource[] }) => (
  <div className='mt-2 flex flex-col gap-1'>
    <Text size={12} color={TextColor.SECONDARY}>
      使用変数と参照元（テーブル.カラム）
    </Text>
    <div className='border-border-input overflow-hidden rounded-lg border'>
      {[SITE_URL_SOURCE, ...params].map((p) => (
        <div
          key={p.token}
          className='border-border-input flex flex-wrap items-center gap-x-3 gap-y-0.5 border-t px-2.5 py-1.5 first:border-t-0'
        >
          <code className='text-size-13 text-emphasis font-mono'>{p.token}</code>
          <span className='text-secondary text-[11px]'>{p.label}</span>
          <code className='text-tertiary ml-auto font-mono text-[11px]'>
            {p.table}.{p.column}
          </code>
        </div>
      ))}
    </div>
  </div>
)

const W2CommerceLinkPage = () => {
  // 上部タブで選択中のバージョン
  const [version, setVersion] = useState<Version>('v5')
  const { data: configs, mutate } = useSystemConfigGetApi()

  // 編集中の値（rules）と 保存済み（saved・比較用）
  const [rules, setRules] = useState<Record<Version, LinkRules>>(emptyAllRules)
  const [saved, setSaved] = useState<Record<Version, LinkRules>>(emptyAllRules)
  const [confirmOpen, setConfirmOpen] = useState(false)

  // 取得完了時に現在値へ反映（保存直後の再取得でも保存値へ同期）
  useEffect(() => {
    if (!configs) return
    const built = buildRules(configs)
    setRules(built)
    setSaved(built)
  }, [configs])

  const showSaved = useShowSavedToast({ editing: true })
  const { trigger, isMutating } = useSystemConfigUpdateApi(() => {
    showSaved('W2Commerceリンク設定')
    setConfirmOpen(false)
    mutate()
  })

  const current = rules[version]
  const setField = (key: keyof LinkRules, v: string) =>
    setRules((prev) => ({ ...prev, [version]: { ...prev[version], [key]: v } }))

  // 変更点（変更前 / 変更後）を抽出
  const changes = VERSIONS.flatMap((v) =>
    LINK_DEFS.filter((d) => rules[v][d.key] !== saved[v][d.key]).map((d) => ({
      key: d.key,
      heading: d.heading,
      version: v,
      before: saved[v][d.key],
      after: rules[v][d.key]
    }))
  )

  const onConfirm = () =>
    trigger({ configs: changes.map((c) => ({ key: cfgKey(c.version, c.key), value: c.after })) })

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='W2Commerceリンク指定'
        description='W2CommerceのURLリンクルールを V5 / V6 別に管理します。保存すると全サイトに即時反映されます。'
      />
      <Content>
        <div className='bg-surface-emphasis text-emphasis rounded-lg px-3 py-2 text-[12px]'>
          <code className='font-mono'>{'{site_url}'}</code> は各サイトの「サイト管理 &gt;
          サイトURL」（<code className='font-mono'>sites.domain</code>
          ）に自動置換されます。ドメイン部分は直接記入せず{' '}
          <code className='font-mono'>{'{site_url}'}</code>{' '}
          を使用してください。ドメイン・パラメータ以外は製品固有パスとして初期値に反映済みです。
        </div>

        <TabList>
          <Tab selected={version === 'v5'} onClick={() => setVersion('v5')}>
            V5
          </Tab>
          <Tab selected={version === 'v6'} onClick={() => setVersion('v6')}>
            V6
          </Tab>
        </TabList>

        {/* 商品へのリンク（入力欄なし・リンクマスタ連動） */}
        <Area>
          <Heading>商品へのリンク</Heading>
          <AreaContent>
            <Text size={12} color={TextColor.SECONDARY}>
              商品リンクマスタ（
              <code className='font-mono'>w2_product_link.product_url</code>
              ）に登録されたURLと連動します。個別のリンクルール設定は不要です（ドメイン部分は{' '}
              <code className='font-mono'>{'{site_url}'}</code> に置換されます）。
            </Text>
            <ParamRefs
              params={[
                {
                  token: '{product_url}',
                  label: '商品リンクURL',
                  table: 'w2_product_link',
                  column: 'product_url'
                }
              ]}
            />
          </AreaContent>
        </Area>

        {/* 記事 / 商品一覧 / 特集カテゴリ / カート投入 / ブランドTOP */}
        {LINK_DEFS.map((def) => (
          <Area key={def.key}>
            <Heading>{def.heading}</Heading>
            <AreaContent>
              <input
                className={`${textInput} w-full`}
                value={current[def.key]}
                placeholder={def.template[version]}
                onChange={(e) => setField(def.key, e.target.value)}
              />
              <ParamRefs params={def.params[version]} />
              {def.note?.[version] && (
                <Text size={12} color={TextColor.SECONDARY}>
                  {def.note[version]}
                </Text>
              )}
            </AreaContent>
          </Area>
        ))}

        <div className='flex items-center gap-3'>
          <Button
            color={ButtonColor.PRIMARY}
            disabled={changes.length === 0}
            onClick={() => setConfirmOpen(true)}
          >
            保存
          </Button>
          <Text size={12} color={TextColor.SECONDARY}>
            {changes.length > 0 ? `${changes.length}件の変更があります` : '変更はありません'}
          </Text>
        </div>
      </Content>

      {/* 変更確認モーダル（変更前 / 変更後 ＋ 全サイト即時反映の警告） */}
      {confirmOpen && changes.length > 0 && (
        <div className='z-modal fixed inset-0 flex items-center justify-center bg-black/60 p-4'>
          <div className='border-secondary bg-surface shadow-area flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl border'>
            <div className='border-secondary border-b px-5 py-3'>
              <Text size={16} weight='bold'>
                変更内容の確認（{changes.length}件）
              </Text>
            </div>
            <div className='bg-surface-critical text-critical mx-5 mt-4 rounded-lg px-3 py-2 text-[13px] font-semibold'>
              ⚠ 保存した瞬間から、全てのサイトのリンク先が変わります。本当に変更してよろしいですか？
            </div>
            <div className='flex flex-col gap-3 overflow-auto px-5 py-4'>
              {changes.map((c) => (
                <div
                  key={`${c.version}-${c.key}`}
                  className='border-secondary rounded-lg border p-3'
                >
                  <Text size={14} weight='bold'>
                    {c.heading}（{c.version.toUpperCase()}）
                  </Text>
                  <div className='text-secondary mt-1.5 text-[11px]'>変更前</div>
                  <code className='bg-surface-secondary text-tertiary block rounded px-2 py-1 font-mono text-[11px] break-all line-through'>
                    {c.before || '（未設定）'}
                  </code>
                  <div className='text-secondary mt-1.5 text-[11px]'>変更後</div>
                  <code className='bg-surface-success text-success block rounded px-2 py-1 font-mono text-[11px] break-all'>
                    {c.after || '（未設定）'}
                  </code>
                </div>
              ))}
            </div>
            <div className='border-secondary flex justify-end gap-2 border-t px-5 py-3'>
              <Button color={ButtonColor.SECONDARY} onClick={() => setConfirmOpen(false)}>
                キャンセル
              </Button>
              <Button color={ButtonColor.CRITICAL} loading={isMutating} onClick={onConfirm}>
                変更を保存
              </Button>
            </div>
          </div>
        </div>
      )}
    </Main>
  )
}

export default W2CommerceLinkPage
